import { LitElement, html, css, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';
import { AutoDismissController } from './auto-dismiss.controller';

export type AlertVariant = 'default' | 'success' | 'danger';
export type AlertDisplay = 'floating' | 'full-width';

export interface AscDismissDetail {
  /** Whether the dismiss was triggered by the user or by the auto-dismiss timer. */
  trigger: 'user' | 'auto';
}

/**
 * A contextual message banner for communicating system feedback, status changes, or
 * important notices to the user.
 *
 * Use `display="floating"` (default) for transient notifications that overlay existing
 * content, and `display="full-width"` when embedding the alert inline within the page layout.
 *
 * A dismiss button is always rendered. When `auto-dismiss` is set the element removes
 * itself from the DOM after `dismiss-duration` milliseconds. Listen to `asc-dismiss` to
 * respond to either kind of dismissal.
 *
 * ```html
 * <asc-alert variant="success">
 *   <svg slot="icon" aria-hidden="true" viewBox="0 0 24 24">...</svg>
 *   Changes saved
 *   <span slot="subheadline">Your profile has been updated.</span>
 * </asc-alert>
 * ```
 *
 * @element asc-alert
 *
 * @fires asc-dismiss - Fired immediately before the element removes itself from the DOM.
 *   Detail: `{ trigger: 'user' | 'auto' }`
 *
 * @slot           - **Required.** The primary headline for the alert. Accepts inline text
 *   or a heading element (`<h2>`–`<h6>`).
 * @slot subheadline - Optional longer description. Hidden when empty.
 * @slot icon      - Optional leading icon. Accepts `<svg aria-hidden="true">`. The icon
 *   is presentational; the `role="alert"` region provides semantic context. Hidden when empty.
 * @slot actions   - Optional action controls beyond dismissing. Accepts `<button>` or `<a>`
 *   elements **directly** (not wrapped in a container — `::slotted()` only styles immediate
 *   children). The first element receives the primary/brand style; subsequent elements receive
 *   a secondary style. Hidden when empty.
 *
 * @csspart base        - The root `<section>` — layout, padding, border, background.
 * @csspart icon        - `<span>` wrapping the `icon` slot.
 * @csspart body        - `<div>` wrapping headline, subheadline, and actions slots.
 * @csspart headline    - `<span>` wrapping the default slot.
 * @csspart subheadline - `<span>` wrapping the `subheadline` slot.
 * @csspart actions     - `<div>` wrapping the `actions` slot.
 * @csspart dismiss     - The dismiss `<button>`.
 *
 * @cssprop --asc-alert-bg                      - Background (default variant, floating). Default: `var(--asc-bg-primary_alt)`
 * @cssprop --asc-alert-bg-full-width            - Background (default variant, full-width). Default: `var(--asc-bg-primary)`
 * @cssprop --asc-alert-bg-success              - Background (success variant). Default: `var(--asc-bg-success-primary)`
 * @cssprop --asc-alert-bg-danger               - Background (danger variant). Default: `var(--asc-bg-error-primary)`
 * @cssprop --asc-alert-border-color            - Border color (default variant). Default: `var(--asc-border-primary)`
 * @cssprop --asc-alert-border-color-success    - Border color (success variant). Default: `var(--asc-border-success)`
 * @cssprop --asc-alert-border-color-danger     - Border color (danger variant). Default: `var(--asc-border-error)`
 * @cssprop --asc-alert-border-radius           - Border radius (floating). Default: `var(--asc-radius-lg)`
 * @cssprop --asc-alert-border-radius-full-width - Border radius (full-width). Default: `var(--asc-radius-none)`
 * @cssprop --asc-alert-border-width            - Border width. Default: `var(--asc-border-width)`
 * @cssprop --asc-alert-padding                 - Internal padding (floating). Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-alert-gap                     - Gap between icon, body, and dismiss (floating). Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-alert-gap-full-width          - Gap between content items (full-width). Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-alert-body-gap               - Gap between headline and subheadline. Default: `var(--asc-spacing-xs)`
 * @cssprop --asc-alert-actions-gap            - Gap between action items. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-alert-headline-color         - Headline text color (default variant). Default: `var(--asc-text-secondary)`
 * @cssprop --asc-alert-headline-color-success - Headline text color (success variant). Default: `var(--asc-text-success-primary)`
 * @cssprop --asc-alert-headline-color-danger  - Headline text color (danger variant). Default: `var(--asc-text-error-primary)`
 * @cssprop --asc-alert-subheadline-color      - Subheadline text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-alert-icon-color             - Icon color (default variant). Default: `var(--asc-fg-secondary)`
 * @cssprop --asc-alert-icon-color-success     - Icon color (success variant). Default: `var(--asc-fg-success-primary)`
 * @cssprop --asc-alert-icon-color-danger      - Icon color (danger variant). Default: `var(--asc-fg-error-primary)`
 * @cssprop --asc-alert-dismiss-color          - Dismiss button color. Default: `var(--asc-fg-tertiary)`
 * @cssprop --asc-alert-dismiss-color-hover    - Dismiss button hover color. Default: `var(--asc-fg-primary)`
 * @cssprop --asc-alert-shadow                 - Box shadow (floating only). Default: `0px 1px 2px 0px var(--asc-shadow-xs)`
 * @cssprop --asc-alert-max-width              - Maximum width (floating only). Default: `var(--asc-width-xs)`
 * @cssprop --asc-alert-z-index                - Z-index (floating only). Default: `var(--asc-z-fixed)`
 * @cssprop --asc-alert-action-primary-bg      - Background of the first action button (full-width). Defaults to the variant's solid color.
 * @cssprop --asc-alert-action-primary-bg-hover - Hover background of the first action button (full-width). Defaults to the variant's solid hover color.
 */
@customElement('asc-alert')
export class AscAlert extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    /* ─── Base layout ─────────────────────────────────────────── */
    [part='base'] {
      position: relative;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: var(--asc-alert-gap, var(--asc-spacing-xl));
      padding: var(--asc-alert-padding, var(--asc-spacing-xl));
      border-style: solid;
      border-width: var(--asc-alert-border-width, var(--asc-border-width));
      font-family: var(--asc-font-family-body);
      box-sizing: border-box;
    }

    /* ─── Floating ────────────────────────────────────────────── */
    :host([display='floating']) [part='base'],
    :host(:not([display])) [part='base'] {
      background-color: var(--asc-alert-bg, var(--asc-bg-primary_alt));
      border-color: var(--asc-alert-border-color, var(--asc-border-primary));
      border-radius: var(--asc-alert-border-radius, var(--asc-radius-lg));
      box-shadow: var(--asc-alert-shadow, 0px 1px 2px 0px var(--asc-shadow-xs));
      max-width: var(--asc-alert-max-width, var(--asc-width-xs));
      z-index: var(--asc-alert-z-index, var(--asc-z-fixed));
    }

    /* ─── Full-width ──────────────────────────────────────────── */
    :host([display='full-width']) [part='base'] {
      background-color: var(--asc-alert-bg-full-width, var(--asc-bg-primary));
      border-color: var(--asc-alert-border-color, var(--asc-border-primary));
      border-radius: var(--asc-alert-border-radius-full-width, var(--asc-radius-none));
      border-top-width: 0;
      border-left-width: 0;
      border-right-width: 0;
      align-items: flex-start;
      width: 100%;
      max-width: none;
      gap: var(--asc-alert-gap-full-width, var(--asc-spacing-lg));
      padding: var(--asc-spacing-lg) var(--asc-container-padding-desktop);
    }

    /* ─── Variant: success ────────────────────────────────────── */
    :host([variant='success']) [part='base'] {
      background-color: var(--asc-alert-bg-success, var(--asc-bg-success-primary));
      border-color: var(--asc-alert-border-color-success, var(--asc-border-success));
    }

    :host([variant='success']) [part='headline'] {
      color: var(--asc-alert-headline-color-success, var(--asc-text-success-primary));
    }

    :host([variant='success']) [part='icon'] {
      color: var(--asc-alert-icon-color-success, var(--asc-fg-success-primary));
    }

    :host([variant='success']) {
      --asc-alert-action-primary-bg: var(--asc-bg-success-solid);
      --asc-alert-action-primary-bg-hover: var(--asc-bg-success-solid_hover);
    }

    /* ─── Variant: danger ─────────────────────────────────────── */
    :host([variant='danger']) [part='base'] {
      background-color: var(--asc-alert-bg-danger, var(--asc-bg-error-primary));
      border-color: var(--asc-alert-border-color-danger, var(--asc-border-error));
    }

    :host([variant='danger']) [part='headline'] {
      color: var(--asc-alert-headline-color-danger, var(--asc-text-error-primary));
    }

    :host([variant='danger']) [part='icon'] {
      color: var(--asc-alert-icon-color-danger, var(--asc-fg-error-primary));
    }

    :host([variant='danger']) {
      --asc-alert-action-primary-bg: var(--asc-bg-error-solid);
      --asc-alert-action-primary-bg-hover: var(--asc-bg-error-solid_hover);
    }

    /* ─── Icon wrapper ────────────────────────────────────────── */
    [part='icon'] {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 48px;
      height: 48px;
      overflow: hidden;
      background-color: var(--asc-bg-secondary);
      border: 1px solid var(--asc-alpha-black-10);
      border-radius: var(--asc-radius-sm);
      box-shadow:
        0px 1px 2px 0px var(--asc-shadow-xs),
        inset 0px 0px 1px 1px var(--asc-shadow-skeumorphic-inner),
        inset 0px -1px 1px 1px var(--asc-shadow-skeumorphic-inner);
      color: var(--asc-alert-icon-color, var(--asc-fg-secondary));
    }

    /* Full-width: icon is a circle with no border or shadow */
    :host([display='full-width']) [part='icon'] {
      border-radius: var(--asc-radius-full);
      border: none;
      box-shadow: none;
    }

    [part='icon'][hidden] {
      display: none;
    }

    /* ─── Body ────────────────────────────────────────────────── */
    [part='body'] {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: var(--asc-alert-body-gap, var(--asc-spacing-xs));
    }

    /* Full-width: headline + subheadline run inline; subheadline wraps below when too long */
    :host([display='full-width']) [part='body'] {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: baseline;
      column-gap: var(--asc-spacing-sm);
    }

    /* ─── Headline ────────────────────────────────────────────── */
    [part='headline'] {
      display: block;
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-semibold); /* semibold */
      line-height: var(--asc-line-height-text-sm);
      color: var(--asc-alert-headline-color, var(--asc-text-secondary));
      /* Reserve space for the absolutely positioned dismiss button */
      padding-right: var(--asc-spacing-4xl);
    }

    :host([display='full-width']) [part='headline'] {
      padding-right: 0;
      flex-shrink: 0;
      white-space: nowrap;
    }

    /* ─── Subheadline ─────────────────────────────────────────── */
    [part='subheadline'] {
      display: block;
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-regular); /* regular */
      line-height: var(--asc-line-height-text-sm);
      color: var(--asc-alert-subheadline-color, var(--asc-text-tertiary));
    }

    /* Full-width: subheadline fills remaining space; wraps to next line when too long */
    :host([display='full-width']) [part='subheadline'] {
      flex: 1 1 auto;
      order: 2;
    }

    [part='subheadline'][hidden] {
      display: none;
    }

    /* ─── Actions ─────────────────────────────────────────────── */
    [part='actions'] {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      gap: var(--asc-alert-actions-gap, var(--asc-spacing-lg));
      margin-top: var(--asc-spacing-md);
    }

    [part='actions'][hidden] {
      display: none;
    }

    /* Full-width: actions sit at the trailing edge of row 1, never wrap */
    :host([display='full-width']) [part='actions'] {
      margin-top: 0;
      margin-left: auto;
      flex-shrink: 0;
      order: 1;
    }

    /* ─── Slotted actions — shared base ────────────────────────── */
    ::slotted(a),
    ::slotted(button) {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-medium); /* medium */
      line-height: var(--asc-line-height-text-sm);
      white-space: nowrap;
      cursor: pointer;
      margin: 0;
      box-sizing: border-box;
      transition: background-color var(--asc-transition-fast),
        color var(--asc-transition-fast);
    }

    ::slotted(a:focus-visible),
    ::slotted(button:focus-visible) {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }

    /* ─── Floating: plain text-link actions ────────────────────── */
    :host([display='floating']) ::slotted(a),
    :host([display='floating']) ::slotted(button),
    :host(:not([display])) ::slotted(a),
    :host(:not([display])) ::slotted(button) {
      background: none;
      border: none;
      padding: 0;
      text-decoration: none;
    }

    /* First action: brand green */
    :host([display='floating']) ::slotted(a:first-of-type),
    :host([display='floating']) ::slotted(button:first-of-type),
    :host(:not([display])) ::slotted(a:first-of-type),
    :host(:not([display])) ::slotted(button:first-of-type) {
      color: var(--asc-text-brand-secondary);
    }

    :host([display='floating']) ::slotted(a:first-of-type:hover),
    :host([display='floating']) ::slotted(button:first-of-type:hover),
    :host(:not([display])) ::slotted(a:first-of-type:hover),
    :host(:not([display])) ::slotted(button:first-of-type:hover) {
      text-decoration: underline;
    }

    /* Subsequent actions: tertiary gray */
    :host([display='floating']) ::slotted(a:not(:first-of-type)),
    :host([display='floating']) ::slotted(button:not(:first-of-type)),
    :host(:not([display])) ::slotted(a:not(:first-of-type)),
    :host(:not([display])) ::slotted(button:not(:first-of-type)) {
      color: var(--asc-text-tertiary);
    }

    :host([display='floating']) ::slotted(a:not(:first-of-type):hover),
    :host([display='floating']) ::slotted(button:not(:first-of-type):hover),
    :host(:not([display])) ::slotted(a:not(:first-of-type):hover),
    :host(:not([display])) ::slotted(button:not(:first-of-type):hover) {
      text-decoration: underline;
    }

    /* ─── Full-width: styled button actions ─────────────────────── */
    :host([display='full-width']) ::slotted(a),
    :host([display='full-width']) ::slotted(button) {
      border-style: solid;
      border-width: 1px;
      border-radius: var(--asc-radius-sm);
      padding: var(--asc-spacing-sm) var(--asc-spacing-xl);
      text-decoration: none;
    }

    /* First action: solid brand fill */
    :host([display='full-width']) ::slotted(a:first-of-type),
    :host([display='full-width']) ::slotted(button:first-of-type) {
      background-color: var(--asc-alert-action-primary-bg, var(--asc-bg-brand-solid));
      color: var(--asc-text-white);
      border-color: transparent;
    }

    :host([display='full-width']) ::slotted(a:first-of-type:hover),
    :host([display='full-width']) ::slotted(button:first-of-type:hover) {
      background-color: var(--asc-alert-action-primary-bg-hover, var(--asc-bg-brand-solid_hover));
    }

    /* Subsequent actions: outlined ghost */
    :host([display='full-width']) ::slotted(a:not(:first-of-type)),
    :host([display='full-width']) ::slotted(button:not(:first-of-type)) {
      background-color: transparent;
      color: var(--asc-text-secondary);
      border-color: var(--asc-border-primary);
    }

    :host([display='full-width']) ::slotted(a:not(:first-of-type):hover),
    :host([display='full-width']) ::slotted(button:not(:first-of-type):hover) {
      background-color: var(--asc-bg-primary_hover);
    }

    /* ─── Dismiss button ──────────────────────────────────────── */
    [part='dismiss'] {
      position: absolute;
      top: var(--asc-spacing-md);
      right: var(--asc-spacing-md);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 36px;
      height: 36px;
      padding: var(--asc-spacing-md);
      background-color: transparent;
      border: none;
      border-radius: var(--asc-radius-md);
      cursor: pointer;
      color: var(--asc-alert-dismiss-color, var(--asc-fg-tertiary));
      transition: color var(--asc-transition-fast),
        background-color var(--asc-transition-fast);
      box-sizing: border-box;
    }

    [part='dismiss']:hover {
      color: var(--asc-alert-dismiss-color-hover, var(--asc-fg-primary));
      background-color: var(--asc-bg-primary_hover);
    }

    [part='dismiss']:focus-visible {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }

    /* Full-width: dismiss sits in normal flow at the trailing edge */
    :host([display='full-width']) [part='dismiss'] {
      position: static;
      flex-shrink: 0;
    }
  `;

  /** Controls the layout mode of the alert. */
  @property({ attribute: 'display', type: String, reflect: true })
  display: AlertDisplay = 'floating';

  /** The semantic color treatment of the alert. */
  @property({ type: String, reflect: true })
  variant: AlertVariant = 'default';

  /** When true, the alert removes itself from the DOM after `dismissDuration` milliseconds. */
  @property({ attribute: 'auto-dismiss', type: Boolean, reflect: true })
  autoDismiss = false;

  /** Duration in milliseconds before auto-dismiss fires. Only used when `autoDismiss` is true. */
  @property({ attribute: 'dismiss-duration', type: Number })
  dismissDuration = 5000;

  @state() private _hasIcon = false;
  @state() private _hasSubheadline = false;
  @state() private _hasActions = false;
  @state() private _ariaLabel = 'Alert';

  constructor() {
    super();
    // Co-located controller that owns the auto-dismiss timer lifecycle.
    // Registered with the host via addController() — no need to retain a field.
    new AutoDismissController(this, () => this._handleDismiss('auto'));
  }

  override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('variant')) {
      this._ariaLabel =
        this.variant === 'success'
          ? 'Success alert'
          : this.variant === 'danger'
            ? 'Danger alert'
            : 'Alert';
    }
  }

  private _handleDismiss(trigger: 'user' | 'auto'): void {
    dispatch<AscDismissDetail>(this, 'asc-dismiss', { detail: { trigger } });
    this.remove();
  }

  private readonly _handleDismissClick = (): void => {
    this._handleDismiss('user');
  };

  private readonly _handleSlotChange = (e: Event): void => {
    const slot = e.target as HTMLSlotElement;
    const name = slot.name as 'icon' | 'subheadline' | 'actions';
    const hasContent = slot.assignedNodes({ flatten: true }).length > 0;
    if (name === 'icon') this._hasIcon = hasContent;
    else if (name === 'subheadline') this._hasSubheadline = hasContent;
    else if (name === 'actions') this._hasActions = hasContent;
  };

  override render() {
    return html`
      <section
        part="base"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        aria-label=${this._ariaLabel}
      >
        <span part="icon" ?hidden=${!this._hasIcon}>
          <slot name="icon" @slotchange=${this._handleSlotChange}></slot>
        </span>

        <div part="body">
          <span part="headline">
            <slot></slot>
          </span>

          <span part="subheadline" ?hidden=${!this._hasSubheadline}>
            <slot name="subheadline" @slotchange=${this._handleSlotChange}></slot>
          </span>

          <div part="actions" ?hidden=${!this._hasActions}>
            <slot name="actions" @slotchange=${this._handleSlotChange}></slot>
          </div>
        </div>

        <button
          part="dismiss"
          type="button"
          aria-label="Dismiss alert"
          @click=${this._handleDismissClick}
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-alert': AscAlert;
  }
}
